// File: src/pages/admin/ChatThread.jsx
// ReyZa Admin Chat Thread - Fixed Layout

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import { useUser } from '../../contexts/UserContext';
import { chatAPI } from '../../services/api';
import './css/AdminChats.css';

const SOCKET_SERVER = "https://reyza.tn";

const ChatThread = () => {
  const { convoId } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [socket, setSocket] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [claimed, setClaimed] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const scrollToBottom = useCallback((smooth = true) => {
    messagesEndRef.current?.scrollIntoView({ 
      behavior: smooth ? 'smooth' : 'auto',
      block: 'end'
    });
  }, []);

  useEffect(() => {
    if (!user?.isAdmin) {
      navigate('/admin');
      return;
    }

    const token = localStorage.getItem('token');
    const newSocket = io(SOCKET_SERVER, {
  path: "/socket.io",
  auth: { token },
  transports: ["websocket"],  
  secure: true,
  withCredentials: true,
  reconnection: true,
});
    
    setSocket(newSocket);

    newSocket.on('connect', () => {
      newSocket.emit('join-convo', convoId);
    });

    newSocket.on('new-message', msg => {
      setMessages(prev => {
        if (prev.some(m => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
      scrollToBottom();
    });

    newSocket.on('user-typing', data => {
      if (data.senderType === 'user') setIsTyping(data.isTyping);
    });

    newSocket.on('admin-joined', () => setClaimed(true));

    fetchThread();

    return () => newSocket.close();
  }, [convoId, user, navigate, scrollToBottom]);

  useEffect(() => {
    scrollToBottom(false);
  }, [messages, scrollToBottom]);

  const fetchThread = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await chatAPI.getConversation(convoId);
      setConversation(data.conversation);
      setMessages(data.messages ?? []);
      setClaimed(!!data.conversation.admin_id);

      if (!data.conversation.admin_id) {
        await handleClaim();
      }
    } catch (err) {
      console.error('Failed to fetch thread:', err);
      if (err.response?.status === 404) {
        setError('Conversation introuvable - elle a peut-etre ete supprimee ou fermee.');
        setTimeout(() => navigate('/admin/chats'), 3000);
      } else {
        setError('Impossible de charger la conversation. Veuillez reessayer.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async () => {
    try {
      await chatAPI.claimConversation(convoId);
      setClaimed(true);
    } catch (err) {
      console.error('Claim failed:', err);
      alert('Impossible de reclamer la conversation - elle est peut-etre deja prise.');
    }
  };

  const sendMessage = () => {
    const content = input.trim();
    if (!content || !claimed || !socket) return;

    socket.emit('send-message', { convoId, content, attachmentUrl: null });
    setInput('');
    socket.emit('typing', { convoId, isTyping: false });
    
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    } else if (socket) {
      socket.emit('typing', { convoId, isTyping: true });
      
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        socket.emit('typing', { convoId, isTyping: false });
      }, 1000);
    }
  };

  const handleClose = async () => {
    if (!window.confirm("Fermer cette conversation ? L'utilisateur ne pourra plus envoyer de messages.")) {
      return;
    }
    
    try {
      await chatAPI.closeConversation(convoId);
      navigate('/admin/chats');
    } catch (err) {
      console.error('Close failed:', err);
      alert('Impossible de fermer la conversation');
    }
  };

  const formatTime = useCallback((ts) => {
    return new Date(ts).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  const getUserDisplay = useCallback(() => {
    if (!conversation) return 'Utilisateur';
    if (conversation.user_email) {
      return conversation.firstName || conversation.user_email;
    }
    return 'Invite';
  }, [conversation]);

  if (loading) {
    return (
      <div className="chat-thread-container">
        <div className="thread-header">
          <div className="header-skeleton">
            <div className="skeleton-avatar"></div>
            <div className="skeleton-text"></div>
          </div>
        </div>
        <div className="chat-loading">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chat-thread-container">
        <div className="thread-header">
          <h2>Erreur</h2>
        </div>
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => navigate('/admin/chats')} className="btn-primary">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Retour aux conversations
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-thread-container">
      <header className="thread-header">
        <div className="thread-info">
          <div className="user-avatar">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 12c2.7 0 5-2.3 5-5s-2.3-5-5-5-5 2.3-5 5 2.3 5 5 5zm0 2c-3.3 0-10 1.7-10 5v3h20v-3c0-3.3-6.7-5-10-5z"/>
            </svg>
          </div>
          <div className="thread-details">
            <h1 className="thread-title">{getUserDisplay()}</h1>
            <p className="thread-subtitle">
              Conversation #{convoId}
              {!claimed && <span className="status-badge unclaimed">Non reclamee</span>}
              {claimed && <span className="status-badge claimed">Reclamee</span>}
            </p>
          </div>
        </div>
        <div className="thread-actions">
          {!claimed ? (
            <button className="btn-claim" onClick={handleClaim}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" 
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Reclamer
            </button>
          ) : (
            <button className="btn-close" onClick={handleClose}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
              Fermer
            </button>
          )}
          <button className="btn-back" onClick={() => navigate('/admin/chats')} aria-label="Retour a la liste">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </div>
      </header>

      <main className="thread-messages" ref={messagesContainerRef}>
        {messages.length === 0 ? (
          <div className="empty-state">
            <p>Aucun message pour le moment. Demarrez la conversation !</p>
          </div>
        ) : (
          <div className="messages-list">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`message ${msg.senderType === 'admin' ? 'message-admin' : 'message-user'}`}
              >
                <div className="message-content">
                  <div className="message-header">
                    <span className="message-author">
                      {msg.senderType === 'admin' ? 'Vous' : getUserDisplay()}
                    </span>
                    <span className="message-time">{formatTime(msg.timestamp)}</span>
                  </div>
                  <div className="message-body">
                    <p>{msg.content}</p>
                    {msg.attachmentUrl && (
                      <img
                        src={msg.attachmentUrl}
                        alt="Piece jointe"
                        className="message-attachment"
                        loading="lazy"
                        decoding="async"
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {isTyping && (
          <div className="typing-indicator" role="status" aria-live="polite">
            <div className="typing-dots">
              <span></span><span></span><span></span>
            </div>
            <span className="typing-text">{getUserDisplay()} ecrit...</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </main>

      <footer className="thread-input-container">
        {claimed ? (
          <div className="input-wrapper">
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Tapez votre message..."
              className="input-field"
              aria-label="Saisie du message"
            />
            <button 
              onClick={sendMessage} 
              disabled={!input.trim()}
              className="btn-send"
              aria-label="Envoyer le message"
            >
              {input.trim() ? (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M12 19V5" />
                </svg>
              )}
            </button>
          </div>
        ) : (
          <div className="input-disabled">
            <p>Vous devez reclamer cette conversation pour envoyer des messages.</p>
            <button className="btn-claim" onClick={handleClaim}>
              Reclamer maintenant
            </button>
          </div>
        )}
      </footer>
    </div>
  );
};

export default ChatThread;
